import {
	CheckCircleOutlined,
	ClockCircleOutlined,
	CloseCircleOutlined,
	InfoCircleOutlined,
} from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Button, Select, Spin, Tag } from "antd";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SmsNavigation } from "../components/SmsNavigation";
import { getSmsLogs } from "../services/sms.api";

const statusOptions = [
	{ label: "Barchasi", value: "all" },
	{ label: "Kutilmoqda (Pending)", value: "pending" },
	{ label: "Yuborilgan (Sent)", value: "sent" },
	{ label: "Etkazilgan (Delivered)", value: "delivered" },
	{ label: "Xatolik (Failed)", value: "failed" },
];

export function SmsLogsPage() {
	const navigate = useNavigate();
	const [currentPage, setCurrentPage] = useState(1);
	const [statusFilter, setStatusFilter] = useState("all");
	const [searchText, _setSearchText] = useState("");
	const pageSize = 10;

	// 1. Fetch SMS Logs
	const { data: logsData, isLoading } = useQuery({
		queryKey: ["smsLogsList", currentPage, statusFilter, searchText],
		queryFn: () =>
			getSmsLogs({
				page: currentPage,
				limit: pageSize,
				status: statusFilter === "all" ? undefined : statusFilter,
				// search filters are handled if backend supports it or client side,
				// let's pass undefined if empty
			}),
	});

	const logs = useMemo(() => logsData?.items ?? [], [logsData]);
	const total = logsData?.meta?.total ?? 0;
	const totalPages = Math.ceil(total / pageSize);

	const getStatusTag = (status: string) => {
		switch (status) {
			case "delivered":
				return (
					<Tag color="success" icon={<CheckCircleOutlined />}>
						Etkazildi
					</Tag>
				);
			case "failed":
				return (
					<Tag color="error" icon={<CloseCircleOutlined />}>
						Xatolik
					</Tag>
				);
			case "sent":
				return (
					<Tag color="processing" icon={<InfoCircleOutlined />}>
						Yuborildi
					</Tag>
				);
			default:
				return (
					<Tag color="default" icon={<ClockCircleOutlined />}>
						Kutilmoqda
					</Tag>
				);
		}
	};

	const formatDateTime = (dateStr: string) => {
		const date = new Date(dateStr);
		const year = date.getFullYear();
		const month = String(date.getMonth() + 1).padStart(2, "0");
		const day = String(date.getDate()).padStart(2, "0");
		const hours = String(date.getHours()).padStart(2, "0");
		const minutes = String(date.getMinutes()).padStart(2, "0");
		return `${year}-${month}-${day} ${hours}:${minutes}`;
	};

	return (
		<div className="crm-sms-logs-page">
			{/* Page Head */}
			<div className="crm-page-head" style={{ marginBottom: "16px" }}>
				<div>
					<h1>SMS moduli</h1>
					<p>Eskiz integratsiyasi orqali tizim balansi va bildirishnomalarni boshqarish</p>
				</div>
				<div className="crm-head-actions">
					<Button className="crm-primary-btn" type="primary" onClick={() => navigate("/sms/send")}>
						SMS yuborish
					</Button>
				</div>
			</div>

			{/* Sub-Navigation Tabs */}
			<SmsNavigation activeTab="logs" />

			{/* Filter row */}
			<div className="crm-card crm-sms-logs-filter-card">
				<div className="crm-sms-logs-filter-row">
					<span className="crm-sms-logs-filter-label">Status bo'yicha</span>
					<Select
						options={statusOptions}
						value={statusFilter}
						onChange={(val) => {
							setStatusFilter(val);
							setCurrentPage(1);
						}}
						className="crm-sms-logs-filter-select"
					/>
				</div>
			</div>

			{/* Logs Table Card */}
			<div className="crm-card crm-table-card">
				<div className="crm-card-head">
					<h3>Yuborilgan SMSlar tarixi</h3>
				</div>
				<div className="crm-table-wrap">
					{isLoading ? (
						<div style={{ textAlign: "center", padding: "40px" }}>
							<Spin />
						</div>
					) : (
						<table className="crm-table">
							<thead>
								<tr>
									<th>Vaqti</th>
									<th>Telefon raqami</th>
									<th>Xabar matni</th>
									<th>Trigger turi</th>
									<th>Narxi (UZS)</th>
									<th>Status</th>
									<th>Xatolik</th>
								</tr>
							</thead>
							<tbody>
								{logs.length === 0 ? (
									<tr>
										<td colSpan={7} style={{ textAlign: "center", padding: "30px 10px" }}>
											<span style={{ color: "var(--crm-ink-3)" }}>Loglar topilmadi</span>
										</td>
									</tr>
								) : (
									logs.map((log) => (
										<tr key={log.id}>
											<td style={{ whiteSpace: "nowrap" }}>{formatDateTime(log.createdAt)}</td>
											<td>{log.phoneNumber}</td>
											<td
												style={{ maxWidth: "350px", overflow: "hidden", textOverflow: "ellipsis" }}
											>
												{log.messageBody}
											</td>
											<td>
												<Tag color={log.triggerType === "manual" ? "blue" : "orange"}>
													{log.triggerType === "manual" ? "Qo'lda" : "Avtomatik"}
												</Tag>
											</td>
											<td style={{ fontWeight: 600 }}>
												{log.totalPrice ? `${(log.totalPrice / 100).toFixed(2)}` : "0.50"}
											</td>
											<td>{getStatusTag(log.status)}</td>
											<td
												style={{
													color: "#cf1322",
													fontSize: "12px",
													maxWidth: "150px",
													overflow: "hidden",
													textOverflow: "ellipsis",
													whiteSpace: "nowrap",
												}}
											>
												{log.errorMessage || "-"}
											</td>
										</tr>
									))
								)}
							</tbody>
						</table>
					)}
				</div>
				<div className="crm-table-foot">
					<span>Jami {total} ta xabar</span>
					{totalPages > 1 && (
						<div className="crm-pager">
							<button
								type="button"
								disabled={currentPage === 1}
								onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
							>
								‹
							</button>
							{Array.from({ length: totalPages }).map((_, i) => (
								<button
									key={`page-${i + 1}`}
									className={currentPage === i + 1 ? "active" : ""}
									type="button"
									onClick={() => setCurrentPage(i + 1)}
								>
									{i + 1}
								</button>
							))}
							<button
								type="button"
								disabled={currentPage === totalPages}
								onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
							>
								›
							</button>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}

export default SmsLogsPage;
